import * as AvatarPrimitive from "@radix-ui/react-avatar";
import * as React from "react";

import ImageWithLoader from "../images/ImageWithLoader";

import { isValidImageSrc } from "@/utils/content/imageHelper";
import { cn } from "@/utils/core/generalUtils";

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-lg",
      className,
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };

type AvatarProps = {
  src?: string;
  name: string;
  bg?: "none" | string;
  size?: string | number;
  shape?: "rounded" | "radius-md";
  titleName?: string;
} & Omit<React.ComponentPropsWithoutRef<typeof Avatar>, "children">;

const DEFAULT_AVATAR_SIZE = "40px";
const AVATAR_SHAPE_CLASS_NAMES = {
  rounded: "rounded-full",
  "radius-md": "rounded-md",
} satisfies Record<NonNullable<AvatarProps["shape"]>, string>;

const getAvatarInitials = (name: string): string => {
  const nameParts = name?.split(" ");
  if (nameParts.length > 1) {
    const firstInitial = nameParts[0]?.charAt(0);
    const secondInitial = nameParts[1]?.charAt(0);

    return (firstInitial || "U") + (secondInitial || "N");
  }

  const [firstNamePart] = nameParts;

  if (firstNamePart && firstNamePart.length > 1) {
    return firstNamePart.charAt(0) + firstNamePart.charAt(1);
  }

  return firstNamePart?.charAt(0) || "U";
};

/**
 * Renders a user avatar with image preview trigger compatibility.
 */
export const SAvatar = React.forwardRef<
  React.ElementRef<typeof Avatar>,
  AvatarProps
>(
  (
    {
      bg = "#E1E6FF",
      name,
      src,
      size,
      shape,
      className,
      style,
      title,
      titleName,
      ...avatarProps
    },
    ref,
  ): React.JSX.Element => {
    const isValidSrc = isValidImageSrc(src);
    const avatarSize = size || DEFAULT_AVATAR_SIZE;
    const avatarShapeClassName = shape ? AVATAR_SHAPE_CLASS_NAMES[shape] : "";

    return (
      <Avatar
        ref={ref}
        style={{
          width: avatarSize,
          height: avatarSize,
          aspectRatio: "1 / 1",
          ...style,
        }}
        className={cn(avatarShapeClassName, className)}
        title={titleName || title || name || ""}
        {...avatarProps}
      >
        {isValidSrc ? (
          <ImageWithLoader
            src={src}
            height={1080}
            width={1920}
            style={{
              width: avatarSize,
              height: avatarSize,
            }}
            alt={`${name || "User"}'s Avatar`}
            className={cn(
              "aspect-square h-full w-full object-cover ",
              avatarShapeClassName,
            )}
          />
        ) : (
          <div
            style={{
              backgroundColor: bg || "#E1E6FF",
              width: avatarSize,
              height: avatarSize,
              fontSize: `${(typeof size === "number" ? size : parseFloat(String(size)) || 40) / 2.5}px`,
            }}
            className={cn(
              "flex items-center justify-center text-lg text-black uppercase",
              avatarShapeClassName,
            )}
            title={name || "Avatar"}
          >
            {getAvatarInitials(name || "undefined")}
          </div>
        )}
      </Avatar>
    );
  },
);
SAvatar.displayName = "SAvatar";
